import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

export interface SidebarContextValue {
  isExpanded: boolean;
  selectedItemId: string | null;
  width: number;
  isFilterOpen: boolean;
  toggle: () => void;
  setExpanded: (expanded: boolean) => void;
  setSelectedItem: (id: string | null) => void;
  setWidth: (width: number) => void;
  toggleFilter: () => void;
  setFilterOpen: (open: boolean) => void;
}

export const SidebarContext = createContext<SidebarContextValue | null>(null);

export const SIDEBAR_MIN_WIDTH = 160;
export const SIDEBAR_MAX_WIDTH = 600;
export const SIDEBAR_DEFAULT_WIDTH = 248;
const SIDEBAR_WIDTH_STORAGE_KEY = 'ic.sidebar.width';

function readPersistedWidth(): number {
  try {
    const raw = window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
    if (!raw) return SIDEBAR_DEFAULT_WIDTH;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return SIDEBAR_DEFAULT_WIDTH;
    return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, parsed));
  } catch {
    return SIDEBAR_DEFAULT_WIDTH;
  }
}

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [width, setWidthState] = useState<number>(() => readPersistedWidth());
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const toggle = useCallback(() => setIsExpanded((prev) => !prev), []);
  const setExpanded = useCallback((expanded: boolean) => setIsExpanded(expanded), []);
  const setSelectedItem = useCallback((id: string | null) => setSelectedItemId(id), []);
  const setWidth = useCallback((next: number) => {
    const clamped = Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, Math.round(next)));
    setWidthState(clamped);
  }, []);
  const setFilterOpen = useCallback((open: boolean) => {
    setIsFilterOpen(open);
    // Opening the filter requires the sidebar to be expanded so the input is visible.
    if (open) setIsExpanded(true);
  }, []);
  const toggleFilter = useCallback(() => {
    setIsFilterOpen((prev) => {
      const next = !prev;
      if (next) setIsExpanded(true);
      return next;
    });
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(width));
    } catch {
      // localStorage unavailable (test env, private mode) — width stays in memory only.
    }
  }, [width]);

  const value = useMemo<SidebarContextValue>(
    () => ({
      isExpanded,
      selectedItemId,
      width,
      isFilterOpen,
      toggle,
      setExpanded,
      setSelectedItem,
      setWidth,
      toggleFilter,
      setFilterOpen,
    }),
    [
      isExpanded,
      selectedItemId,
      width,
      isFilterOpen,
      toggle,
      setExpanded,
      setSelectedItem,
      setWidth,
      toggleFilter,
      setFilterOpen,
    ],
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}
